import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { getFaviconUrl } from '../utils/general';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

/**
 * Search Indicator Component
 * Shows search progress with favicon stack and expandable sources
 */
const SearchIndicator = ({ message }) => {
  const [showSources, setShowSources] = useState(false);
  const [visibleFavicons, setVisibleFavicons] = useState([]);
  const [showBottomGradient, setShowBottomGradient] = useState(false);
  const [showTopGradient, setShowTopGradient] = useState(false);
  const sourcesListRef = useRef(null);
  
  // Combine streaming sources and database sources
  const streamingSources = message?.searchSources || [];
  const databaseSources = message?.searchResults || [];
  const combinedSources = [...streamingSources, ...databaseSources];
  
  // Normalize URL for better deduplication (handles www, trailing slashes, etc.)
  const normalizeUrl = (url) => {
    try {
      const urlObj = new URL(url);
      // Remove www. prefix and trailing slash for comparison
      const normalizedHost = urlObj.hostname.replace(/^www\./, '');
      const normalizedPath = urlObj.pathname.replace(/\/$/, '') || '/';
      return `${normalizedHost}${normalizedPath}${urlObj.search}`;
    } catch (e) {
      // If URL parsing fails, return original for exact matching
      return url.replace(/^www\./, '').replace(/\/$/, '');
    }
  };

  // Deduplicate sources by normalized URL - only show each unique domain/path once
  const allSources = combinedSources.reduce((acc, source) => {
    const normalizedUrl = normalizeUrl(source.url);
    const existingSource = acc.find(s => normalizeUrl(s.url) === normalizedUrl);
    if (!existingSource) {
      acc.push(source);
    }
    return acc;
  }, []);
  
  // Check if the sources list can scroll and update gradient visibility
  const checkScrollable = () => {
    const element = sourcesListRef.current;
    if (!element) return;
    
    const canScroll = element.scrollHeight > element.clientHeight;
    const isAtTop = element.scrollTop <= 5; // 5px threshold
    const isAtBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 5; // 5px threshold
    
    setShowTopGradient(canScroll && !isAtTop);
    setShowBottomGradient(canScroll && !isAtBottom);
  };
  
  // Effect to check scrollable state when sources expand/collapse or change
  useEffect(() => {
    if (showSources) {
      // Small delay to ensure DOM is updated
      setTimeout(checkScrollable, 100);
    }
  }, [showSources, message?.searchSources, message?.searchResults]);
  const hasSearchSources = allSources.length > 0;

  // Extract unique favicons (max 4) from sources
  const getUniqueFavicons = useCallback(() => {
    const validSources = allSources.filter(source => 
      typeof source === 'object' && source.url
    );
    
    const uniqueDomains = new Set();
    const favicons = [];
    
    for (const source of validSources) {
      if (favicons.length >= 4) break;
      
      try {
        const domain = new URL(source.url).hostname;
        if (!uniqueDomains.has(domain)) {
          uniqueDomains.add(domain);
          favicons.push({
            url: getFaviconUrl(source.url),
            domain: domain,
            title: source.title
          });
        }
      } catch {
        // Skip invalid URLs
      }
    }
    
    return favicons;
  }, [allSources]);

  // Update visible favicons with smooth animations - stream them in as sources arrive
  useEffect(() => {
    const newFavicons = getUniqueFavicons();
    
    // Find truly new favicons that haven't been added yet
    const currentDomains = new Set(visibleFavicons.map(f => f.domain));
    const trulyNewFavicons = newFavicons.filter(favicon => 
      !currentDomains.has(favicon.domain) && visibleFavicons.length < 4
    );
    
    // Add new favicons one by one with staggered animation
    trulyNewFavicons.forEach((favicon, index) => {
      setTimeout(() => {
        setVisibleFavicons(prev => {
          // Double-check it's still not present and we're under the limit
          const exists = prev.some(f => f.domain === favicon.domain);
          if (!exists && prev.length < 4) {
            return [...prev, { ...favicon, isNew: true }];
          }
          return prev;
        });
      }, index * 200); // Slightly longer stagger for better visual effect
    });

    // Remove the isNew flag after animation for the new favicons
    if (trulyNewFavicons.length > 0) {
      setTimeout(() => {
        setVisibleFavicons(prev => 
          prev.map(f => ({ ...f, isNew: false }))
        );
      }, trulyNewFavicons.length * 200 + 400);
    }
  }, [allSources.length, streamingSources.length, databaseSources.length]); // React to source count changes

  // Clear favicons when search starts fresh
  useEffect(() => {
    if (message.isSearching && allSources.length === 0) {
      setVisibleFavicons([]);
    }
  }, [message.isSearching, allSources.length]);
  
  // Check for search status from streaming or database
  const hasSearchStatus = message?.searchStatus || message?.isSearching || message?.hasSearched || message?.hasWebSearch;
  
  // Check for HubSpot function status from streaming or database
  const hasHubSpotStatus = message?.hubspotFunction || message?.hasHubSpotFunction;
  
  // Check for retry status
  const hasRetryStatus = message?.isRetrying || message?.retryAttempt;

  // Don't render anything if there's no search-related content
  if (!hasSearchStatus && !hasHubSpotStatus && !hasRetryStatus) {
    return null;
  }
  
  // Detect if this is a HubSpot search
  const isHubSpotSearch = message?.hubspotFunction || message?.searchStatus?.includes('HubSpot') || message?.hasHubSpotFunction;
  
  // Check if this is a batch operation with progress info
  const hasBatchProgress = message?.progressInfo && message?.progressInfo.total > 0;
  
  // Determine search status text
  let searchStatusText;
  
  // Handle retry states first
  if (message?.isRetrying) {
    searchStatusText = message.searchStatus || 'Retrying...';
  } else if (isHubSpotSearch) {
    if (message?.searchStatus?.includes('HubSpot')) {
      searchStatusText = message.searchStatus; // Use streaming status
    } else if (message?.isSearching) {
      searchStatusText = 'Searching HubSpot...';
    } else if (message?.hasSearched) {
      searchStatusText = 'Retrieved HubSpot data';
    } else if (message?.hasHubSpotFunction) {
      // Generate summary from database results with proper grammar
      const hubspotResults = message.hubspotFunctionResults || [];
      
      if (hubspotResults.length > 0) {
        // Group results by object type with proper pluralization
        const typeGroups = {};
        let totalRecords = 0;
        
        hubspotResults.forEach(result => {
          const count = result.recordCount || 0;
          totalRecords += count;
          
          // Extract object type from function name with proper grammar
          let objectType = 'records';
          if (result.functionName) {
            if (result.functionName.includes('contact')) {
              objectType = count === 1 ? 'contact' : 'contacts';
            } else if (result.functionName.includes('compan')) {
              objectType = count === 1 ? 'company' : 'companies';
            } else if (result.functionName.includes('deal')) {
              objectType = count === 1 ? 'deal' : 'deals';
            }
          }
          
          if (!typeGroups[objectType]) {
            typeGroups[objectType] = 0;
          }
          typeGroups[objectType] += count;
        });
        
        // Create summary text with proper grammar
        if (totalRecords > 0) {
          const typeStrings = Object.entries(typeGroups).map(([type, count]) => `${count} ${type}`);
          searchStatusText = `Retrieved ${typeStrings.join(', ')} from HubSpot`;
        } else {
          searchStatusText = 'Accessed HubSpot CRM';
        }
      } else {
        searchStatusText = 'Accessed HubSpot CRM';
      }
    } else {
      searchStatusText = 'HubSpot CRM';
    }
  } else {
    // Regular web search
    if (message?.isSearching) {
      searchStatusText = 'Searching the web...';
    } else if (message?.hasSearched) {
      searchStatusText = 'Searched the web';
    } else if (message?.hasWebSearch) {
      searchStatusText = 'Searched the web';
    } else {
      searchStatusText = 'Search';
    }
  }

  // HubSpot icon component
  const HubSpotIcon = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" shapeRendering="geometricPrecision" textRendering="geometricPrecision" imageRendering="optimizeQuality" fillRule="evenodd" clipRule="evenodd" viewBox="0 0 489 511.8" className={className}>
      <path fill="currentColor" fillRule="nonzero" d="M375.25 168.45V107.5c16.43-7.68 26.97-24.15 27.08-42.29V63.8c0-25.95-21.05-46.99-47-46.99h-1.37c-25.95 0-46.99 21.04-46.99 46.99v1.41a46.985 46.985 0 0027.29 42.3v60.94c-23.13 3.53-44.98 13.18-63.19 27.84L103.88 66.16c1.19-4.29 1.83-8.73 1.89-13.17v-.11C105.77 23.68 82.09 0 52.88 0 23.68 0 0 23.68 0 52.88c0 29.18 23.64 52.85 52.81 52.89 9.17-.08 18.16-2.59 26.06-7.23l164.62 128.07a133.501 133.501 0 00-22.16 73.61c0 27.39 8.46 54.17 24.18 76.58l-50.06 50.06a43.926 43.926 0 00-12.43-1.81c-23.96 0-43.38 19.42-43.38 43.37 0 23.96 19.42 43.38 43.38 43.38 23.95 0 43.37-19.42 43.37-43.38v-.13a41.81 41.81 0 00-2.02-12.5l49.52-49.56a133.687 133.687 0 0081.54 27.78c73.76 0 133.57-59.81 133.57-133.57 0-66.05-48.3-122.2-113.61-132.06l-.14.07zm-20.39 200.4c-36.79-1.52-65.85-31.79-65.85-68.62 0-35.43 26.97-65.06 62.23-68.38h3.62c35.8 2.73 63.46 32.58 63.46 68.48 0 35.91-27.66 65.76-63.45 68.48l-.01.04zm0 0z"/>
    </svg>
  );

  return (
    <div className={`search-indicator-container ${isHubSpotSearch ? 'hubspot-search' : ''}`}>
      <div className="search-indicator-header">
        <div className="search-indicator-main">
          {/* Icon */}
          {message?.isRetrying ? (
            <div className="retry-icon-container">
              <svg className="w-4 h-4 text-orange-500 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
          ) : isHubSpotSearch ? (
            <div className="hubspot-icon-container">
              <HubSpotIcon className="w-4 h-4" />
            </div>
          ) : (
            <div className="favicon-stack">
              {visibleFavicons.map((favicon, index) => (
                <div 
                  key={favicon.domain}
                  className={`favicon-item ${favicon.isNew ? 'favicon-slide-in' : ''}`}
                  style={{ 
                    zIndex: visibleFavicons.length - index,
                    marginLeft: index > 0 ? '-6px' : '0'
                  }}
                  title={favicon.title}
                >
                  <img 
                    src={favicon.url} 
                    alt={`${favicon.domain} favicon`}
                    className="w-5 h-5 rounded-full"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              ))}
            </div>
          )}
          
          {/* Status text */}
          {message?.isSearching ? (
            <SkeletonTheme 
              baseColor={document.documentElement.classList.contains('dark') ? "#999999" : "#9ca3af"} 
              highlightColor={document.documentElement.classList.contains('dark') ? "#bbbbbb" : "#d1d5db"}
            >
              <div 
                style={{
                  width: '90px',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start'
                }}
              >
                <div
                  style={{
                    width: '90px',
                    height: '16px',
                    maskImage: `url("data:image/svg+xml,${encodeURIComponent(`
                      <svg width="90" height="16" viewBox="0 0 90 16" xmlns="http://www.w3.org/2000/svg">
                        <text x="0" y="12" font-family="system-ui, -apple-system, sans-serif" font-size="14" font-weight="500" fill="black">Searching</text>
                      </svg>
                    `)}")`,
                    WebkitMaskImage: `url("data:image/svg+xml,${encodeURIComponent(`
                      <svg width="90" height="16" viewBox="0 0 90 16" xmlns="http://www.w3.org/2000/svg">
                        <text x="0" y="12" font-family="system-ui, -apple-system, sans-serif" font-size="14" font-weight="500" fill="black">Searching</text>
                      </svg>
                    `)}")`,
                    maskSize: 'contain',
                    WebkitMaskSize: 'contain',
                    maskRepeat: 'no-repeat',
                    WebkitMaskRepeat: 'no-repeat',
                    maskPosition: 'left center',
                    WebkitMaskPosition: 'left center'
                  }}
                >
                  <div style={{ width: '100%', height: '100%' }}>
                    <Skeleton 
                      width={90}
                      height={16}
                      style={{ 
                        borderRadius: 0,
                        display: 'block',
                        margin: 0,
                        padding: 0,
                        lineHeight: '1',
                        verticalAlign: 'top'
                      }}
                    />
                  </div>
                </div>
              </div>
            </SkeletonTheme>
          ) : (
            <span className="search-status-text">
              {searchStatusText}
            </span>
          )}
          
          {/* Loading dots or retry indicator */}
          {message?.isRetrying ? (
            <div className="flex items-center ml-2">
              <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              {message?.retryAttempt && message?.maxRetryAttempts && (
                <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                  {message.retryAttempt}/{message.maxRetryAttempts}
                </span>
              )}
            </div>
          ) : message?.isSearching && (
            <div className="search-dots">
              <div className="search-dot"></div>
              <div className="search-dot"></div>
              <div className="search-dot"></div>
            </div>
          )}
          
          {/* Progress indicator for batch operations */}
          {hasBatchProgress && (
            <div className="flex items-center ml-2 text-xs text-gray-500 dark:text-gray-400">
              <span>{message.progressInfo.progress}/{message.progressInfo.total}</span>
              {message.progressInfo.successful > 0 && (
                <span className="ml-1 text-green-600 dark:text-green-400">
                  ✓{message.progressInfo.successful}
                </span>
              )}
              {message.progressInfo.failed > 0 && (
                <span className="ml-1 text-red-600 dark:text-red-400">
                  ✗{message.progressInfo.failed}
                </span>
              )}
            </div>
          )}
          
          {/* Sources toggle */}
          {hasSearchSources && (
            <button
              onClick={() => setShowSources(!showSources)}
              className="search-chevron-button"
            >
              {showSources ? (
                <ChevronUpIcon className="w-3 h-3" />
              ) : (
                <ChevronDownIcon className="w-3 h-3" />
              )}
            </button>
          )}
        </div>
      </div>
      
      {/* Expanded sources section */}
      {showSources && hasSearchSources && (
        <div className="search-sources-expanded">
          <div className="search-sources-list-container">
            {/* Top gradient overlay - only shows when can scroll up */}
            {showTopGradient && (
              <div className="search-sources-top-gradient"></div>
            )}
            <div 
              ref={sourcesListRef}
              className="search-sources-list"
              onScroll={checkScrollable}
            >
              {allSources.map((source, index) => (
                <div key={index} className="search-source-item">
                  <img 
                    src={getFaviconUrl(source.url)} 
                    alt={`${source.url} favicon`}
                    className="w-4 h-4 rounded-full flex-shrink-0"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <a 
                    href={source.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="search-source-link"
                  >
                    <div className="search-source-title">{source.title}</div>
                    <div className="search-source-url">{source.url}</div>
                    {source.pageAge && (
                      <div className="search-source-age">{source.pageAge}</div>
                    )}
                  </a>
                </div>
              ))}
            </div>
            {/* Bottom gradient overlay - only shows when can scroll down */}
            {showBottomGradient && (
              <div className="search-sources-bottom-gradient"></div>
            )}
          </div>
        </div>
      )}
      
      {/* Progress bar for batch operations */}
      {hasBatchProgress && message.progressInfo.total > 1 && (
        <div className="mt-2 w-full bg-gray-200 dark:bg-[#2a2a2a] rounded-full h-1">
          <div 
            className="bg-blue-500 dark:bg-blue-400 h-1 rounded-full transition-all duration-300"
            style={{ 
              width: `${Math.min(100, (message.progressInfo.progress / message.progressInfo.total) * 100)}%` 
            }}
          />
        </div>
      )}
    </div>
  );
};

export default SearchIndicator; 